import {
	createSlice,
	createAsyncThunk
} from '@reduxjs/toolkit'
import callApi from '../../../helpers/callApi'
import { setCompany } from '../manager/managerSlice'
import { changeUserCompany } from '../user/userSlice'

// Remove fetchAddons thunk since addons come with plans

const initialState = {
	// Current step
	currentStep: 1,

	// Billing frequency
	billingFrequency: 'annually',

	// Selected plan
	selectedPlan: null, // Will be created when addons are loaded or plan is selected
	
	// Full plan card data for tracking selected plan in new design
	selectedPlanCardData: null, // Full cardData object when plan is selected

	// Selected tab index (for restoring tab when returning from checkout)
	// Main tabs: VAT PRICING (0), SALES TAX PRICING (1), Bundles (2)
	selectedTab: 0,
	
	// Selected addon tab index (for addon tabs in checkout)
	// Addon tabs: One set for all periods (0), Different set for each period (1)
	selectedAddonTab: 0,

	// Currency from server (usd, eur, gbp, etc.)
	currency: null,

	// Period and country settings
	periodSettings: {
		// For TAX plans
		country: null,
		fromDate: null,
		toDate: null,

		// For EPR plans
		year: null,
		category: null
	},

	// Selected addons
	selectedAddons: {
		// Dynamic structure based on server addons
		quantities: {},
		instances: {},
		metadata: {},
		// EPR-specific: store selected types for addons
		types: {}
	},

	// Checkout addons (for new checkout flow)
	checkoutAddons: [], // Array of added addons in checkout

	// Step validation
	stepValidation: {
		step1: false, // Plan selected
		step2: false, // Period configured
		step3: false  // Addons selected
	}
}

export const subscriptionBuilderSlice = createSlice({
	name: 'subscriptionBuilder',
	initialState,
	reducers: {
	setSelectedPlan: (state, action) => {
		// If null is passed, clear the selected plan
		if (action.payload === null) {
			state.selectedPlan = null
			return
		}

		// If selectedPlan doesn't exist, create it
		if (!state.selectedPlan) {
			state.selectedPlan = {
				planId: null,
				planName: null,
				planType: null,
				billingFrequency: 'annually',
				price: null,
				currency: null,
				addons: []
			}
		}

		// Update plan, using addons from payload if available
		state.selectedPlan = {
			...state.selectedPlan,
			...action.payload,
			// If payload has addons, use them, otherwise keep existing ones
			addons: action.payload.addons || state.selectedPlan.addons || []
		}

		// Dynamically initialize addon structure based on server data
		if (action.payload.addons && Array.isArray(action.payload.addons)) {
				action.payload.addons.forEach(addon => {
					const addonName = addon.name || addon.type || addon.id
					
					// Initialize quantities by addon name
					if (!state.selectedAddons.quantities.hasOwnProperty(addonName)) {
						state.selectedAddons.quantities[addonName] = 0
					}
					
					// Initialize instances array by addon name
					if (!state.selectedAddons.instances.hasOwnProperty(addonName)) {
						state.selectedAddons.instances[addonName] = []
					}
					
					// Initialize metadata by addon name
					if (!state.selectedAddons.metadata.hasOwnProperty(addonName)) {
						// Default metadata structure - can be customized based on addon requirements
						state.selectedAddons.metadata[addonName] = {
							requiredFields: ['fromDate', 'toDate'],
							optionalFields: [],
							fieldTypes: {
								fromDate: 'date',
								toDate: 'date'
							}
						}
						
						// Add country field if addon requires it
						if (addon.requiresCountry !== false) {
							state.selectedAddons.metadata[addonName].requiredFields.unshift('country')
							state.selectedAddons.metadata[addonName].fieldTypes.country = 'select'
						}
						
						// Add custom fields based on addon configuration
						if (addon.customFields) {
							addon.customFields.forEach(field => {
								state.selectedAddons.metadata[addonName].optionalFields.push(field.name)
								state.selectedAddons.metadata[addonName].fieldTypes[field.name] = field.type || 'text'
							})
						}
					}
				})
			}

			// Also update top-level billingFrequency if it's changed in the plan
			if (action.payload.billingFrequency) {
				state.billingFrequency = action.payload.billingFrequency
			}
			state.stepValidation.step1 = true
		},

		setBillingFrequency: (state, action) => {
			state.billingFrequency = action.payload
			// Also update billingFrequency in selectedPlan if plan is selected
			if (state.selectedPlan) {
				state.selectedPlan.billingFrequency = action.payload
			}
		},

		setPeriodSettings: (state, action) => {
			// Convert Date objects to ISO strings for serialization
			const serializedPayload = { ...action.payload }
			if (serializedPayload.fromDate instanceof Date) {
				serializedPayload.fromDate = serializedPayload.fromDate.toISOString()
			}
			if (serializedPayload.toDate instanceof Date) {
				serializedPayload.toDate = serializedPayload.toDate.toISOString()
			}
			
			state.periodSettings = {...state.periodSettings, ...serializedPayload}
			state.stepValidation.step2 = true
		},

		setSelectedAddons: (state, action) => {
			// Handle both old format (direct properties) and new format (quantities, instances, metadata)
			if (action.payload.quantities || action.payload.instances || action.payload.metadata) {
				// New format
				state.selectedAddons = {...state.selectedAddons, ...action.payload}
			} else {
				// Old format - update quantities for backward compatibility
				Object.keys(action.payload).forEach(addonType => {
					if (state.selectedAddons.quantities.hasOwnProperty(addonType)) {
						state.selectedAddons.quantities[addonType] = action.payload[addonType]
					}
				})
			}
			
			// Also update addons in selectedPlan for backward compatibility
			if (state.selectedPlan && state.selectedPlan.addons) {
				state.selectedPlan.addons = {...state.selectedPlan.addons, ...action.payload}
			}
			state.stepValidation.step3 = true
		},

		setSelectedPlanAddons: (state, action) => {
			if (state.selectedPlan && state.selectedPlan.addons) {
				state.selectedPlan.addons = action.payload
			}
		},

		updateAddonQuantity: (state, action) => {
			const {
				addonType,
				quantity
			} = action.payload
			
			// Update quantities in selectedAddons
			if (state.selectedAddons.quantities.hasOwnProperty(addonType)) {
				state.selectedAddons.quantities[addonType] = quantity
			}
			
			// Update quantity in selectedPlan for backward compatibility
			if (state.selectedPlan && state.selectedPlan.addons) {
				const addonIndex = state.selectedPlan.addons.findIndex(addon => 
					(addon.type === addonType) || (addon.id === addonType)
				)
				if (addonIndex !== -1) {
					state.selectedPlan.addons[addonIndex].quantity = quantity
				}
			}
			
		},

		// Add new addon instance
		addAddonInstance: (state, action) => {
			const { addonId, instanceData } = action.payload
			
			// Find the addon to get its name and type
			const addon = state.selectedPlan?.addons?.find(a => String(a.id) === String(addonId))
			const addonName = addon?.name || addon?.type || addonId
			
			// Initialize instances array if it doesn't exist
			if (!state.selectedAddons.instances[addonName]) {
				state.selectedAddons.instances[addonName] = []
			}
			
			// Convert Date objects to ISO strings for serialization
			const serializedInstanceData = { ...instanceData }
			if (serializedInstanceData.fromDate instanceof Date) {
				serializedInstanceData.fromDate = serializedInstanceData.fromDate.toISOString()
			}
			if (serializedInstanceData.toDate instanceof Date) {
				serializedInstanceData.toDate = serializedInstanceData.toDate.toISOString()
			}
			
			// Check if instance with this number already exists
			const existingInstanceIndex = state.selectedAddons.instances[addonName].findIndex(
				instance => instance.instanceNumber === serializedInstanceData.instanceNumber
			)
			
			if (existingInstanceIndex !== -1) {
				// Update existing instance
				state.selectedAddons.instances[addonName][existingInstanceIndex] = {
					...state.selectedAddons.instances[addonName][existingInstanceIndex],
					...serializedInstanceData,
					addonType: addon?.type || addonId,
					addonName: addonName
				}
			} else {
				// Add new instance
				state.selectedAddons.instances[addonName].push({
					id: `addon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
					addonId,
					addonType: addon?.type || addonId,
					addonName: addonName,
					...serializedInstanceData
				})
			}
		},

		// Update addon instance
		updateAddonInstance: (state, action) => {
			const { addonId, instanceId, updates } = action.payload
			
			// Find the addon to get its name
			const addon = state.selectedPlan?.addons?.find(a => String(a.id) === String(addonId))
			const addonName = addon?.name || addon?.type || addonId
			
			if (state.selectedAddons.instances[addonName]) {
				const instanceIndex = state.selectedAddons.instances[addonName].findIndex(
					instance => instance.id === instanceId
				)
				
				if (instanceIndex !== -1) {
					// Convert Date objects to ISO strings for serialization
					const serializedUpdates = { ...updates }
					if (serializedUpdates.fromDate instanceof Date) {
						serializedUpdates.fromDate = serializedUpdates.fromDate.toISOString()
					}
					if (serializedUpdates.toDate instanceof Date) {
						serializedUpdates.toDate = serializedUpdates.toDate.toISOString()
					}
					
					state.selectedAddons.instances[addonName][instanceIndex] = {
						...state.selectedAddons.instances[addonName][instanceIndex],
						...serializedUpdates
					}
				}
			}
		},

		// Remove addon instance
		removeAddonInstance: (state, action) => {
			const { addonId, instanceId } = action.payload
			
			// Find the addon to get its name
			const addon = state.selectedPlan?.addons?.find(a => String(a.id) === String(addonId))
			const addonName = addon?.name || addon?.type || addonId
			
			if (state.selectedAddons.instances[addonName]) {
				state.selectedAddons.instances[addonName] = state.selectedAddons.instances[addonName].filter(
					instance => instance.id !== instanceId
				)
			}
		},

	goToStep: (state, action) => {
		state.currentStep = action.payload
	},

	resetBuilder: () => {
		return { 
			...initialState,
			selectedAddonTab: 0 // Reset addon tab to default
		}
	},

		updateStepValidation: (state, action) => {
			const {
				step,
				isValid
			} = action.payload
			state.stepValidation[step] = isValid
		},

		// Set currency from server
		setCurrency: (state, action) => {
			state.currency = action.payload
		},

		// Reset only addons data (keep plan and period settings)
		resetAddons: (state) => {
			state.selectedAddons = {
				quantities: {},
				instances: {},
				metadata: {},
				types: {} // Reset EPR types as well
			}
			// Reset checkout addons as well
			state.checkoutAddons = []
			// Reset step 3 validation since addons are cleared
			state.stepValidation.step3 = false
		},
		
		// Set full plan card data when plan is selected
		setSelectedPlanCardData: (state, action) => {
			state.selectedPlanCardData = action.payload
		},
		
		// Clear selected plan card data
		clearSelectedPlanCardData: (state) => {
			state.selectedPlanCardData = null
		},
		
		// Set selected tab index (main tabs: VAT, Sales Tax, Credits)
		setSelectedTabIndex: (state, action) => {
			state.selectedTab = action.payload
		},
		
		// Set selected addon tab index (addon tabs: One set for all periods, Different set for each period).
		// Only updates the tab; does not clear plan (selectedPlanCardData) or period (periodSettings).
		setSelectedAddonTabIndex: (state, action) => {
			state.selectedAddonTab = action.payload
		},

		// Add checkout addon
		addCheckoutAddon: (state, action) => {
			const addonData = action.payload
			// Convert Date objects to ISO strings for serialization
			const serializedAddon = { ...addonData }
			if (serializedAddon.dateFrom instanceof Date) {
				serializedAddon.dateFrom = serializedAddon.dateFrom.toISOString()
			}
			if (serializedAddon.dateTill instanceof Date) {
				serializedAddon.dateTill = serializedAddon.dateTill.toISOString()
			}
			
			state.checkoutAddons.push(serializedAddon)
		},

		// Remove checkout addon
		removeCheckoutAddon: (state, action) => {
			const { addonId } = action.payload
			state.checkoutAddons = state.checkoutAddons.filter(addon => addon.id !== addonId)
		},

		// Clear all checkout addons
		clearCheckoutAddons: (state) => {
			state.checkoutAddons = []
		}
	},
	extraReducers: (builder) => {
		// Remove fetchAddons extraReducers since addons come with plans
		
		// Reset subscription builder data when company is changed
		builder
			.addCase(setCompany.fulfilled, (state) => {
				// Reset all subscription-related data when company changes
				state.currency = null
				state.checkoutAddons = []
				state.selectedAddons = {
					quantities: {},
					instances: {},
					metadata: {},
					types: {}
				}
				state.periodSettings = {
					country: null,
					fromDate: null,
					toDate: null,
					year: null,
					category: null
				}
				state.selectedPlanCardData = null
				state.selectedPlan = null
			})
			.addCase(changeUserCompany.fulfilled, (state) => {
				// Reset all subscription-related data when company changes via ChangeCompanyModal
				state.currency = null
				state.checkoutAddons = []
				state.selectedAddons = {
					quantities: {},
					instances: {},
					metadata: {},
					types: {}
				}
				state.periodSettings = {
					country: null,
					fromDate: null,
					toDate: null,
					year: null,
					category: null
				}
				state.selectedPlanCardData = null
				state.selectedPlan = null
			})
	}
})

export const {
	setSelectedPlan,
	setBillingFrequency,
	setPeriodSettings,
	setSelectedAddons,
	setSelectedPlanAddons,
	updateAddonQuantity,
	addAddonInstance,
	updateAddonInstance,
	removeAddonInstance,
	goToStep,
	resetBuilder,
	updateStepValidation,
	setCurrency,
	resetAddons,
	setSelectedPlanCardData,
	clearSelectedPlanCardData,
	setSelectedTabIndex,
	setSelectedAddonTabIndex,
	addCheckoutAddon,
	removeCheckoutAddon,
	clearCheckoutAddons
} = subscriptionBuilderSlice.actions

export default subscriptionBuilderSlice.reducer
